import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

interface LocationState {
  userId?: string;
  email?: string;
  emailCode?: string;
}

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [userId] = useState(state?.userId || '');
  const [email] = useState(state?.email || '');
  const [code, setCode] = useState(state?.emailCode || '');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendCooldown > 0) {
      return;
    }

    setResending(true);
    setError('');
    setResendMessage('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/custom/resend-verification`,
        {
          userId,
          email,
        }
      );

      // If in development, auto-fill the code
      if (response.data.emailCode) {
        setCode(response.data.emailCode);
      }

      setResendMessage('Verification email sent! Check your inbox.');
      setResendCooldown(60); // 60 second cooldown
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to resend verification email';
      const retryAfter = err.response?.data?.retryAfter;

      if (retryAfter) {
        setResendCooldown(retryAfter);
        setError(errorMessage);
      } else {
        setError(errorMessage);
      }
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId) {
      setError('User ID is missing. Please register again.');
      return;
    }

    if (!code) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/auth/custom/verify-email`,
        {
          userId,
          code,
        }
      );

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify email. The code may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Email Verification</h1>
          </div>

          <div className="error-message">
            <p>No user information found. Please register or try again.</p>
          </div>

          <button
            onClick={() => navigate('/register')}
            className="submit-button"
          >
            Go to Register
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Email Verified!</h1>
            <p>Your email has been successfully verified</p>
          </div>

          <div className="success-message">
            <p>You can now login to your account.</p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="submit-button"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Verify Your Email</h1>
          <p>Enter the verification code sent to your email</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {resendMessage && <div className="success-message">{resendMessage}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              name="code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
                setResendMessage('');
              }}
              placeholder="Enter verification code"
              disabled={loading}
              autoComplete="off"
              maxLength={20}
              autoFocus
              required
            />
            <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
              Check your email for the verification code
            </small>
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>

          <button
            type="button"
            onClick={handleResendVerification}
            className="back-button"
            disabled={loading || resending || resendCooldown > 0}
            style={{ marginTop: '10px', backgroundColor: '#4A90E2' }}
          >
            {resending
              ? 'Sending...'
              : resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend Verification Email'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/register')}
            className="back-button"
            disabled={loading}
            style={{ marginTop: '10px' }}
          >
            ← Back to Register
          </button>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;
